import { Children, forwardRef, useLayoutEffect, useRef } from "react";
import {
  AnimatePresence,
  LayoutGroup,
  motion,
  useIsPresent,
  useReducedMotion,
} from "motion/react";
import { FiChevronRight } from "react-icons/fi";
import { spring } from "../lib/springs";

/* ============================================================================
   Breadcrumb — hand-ported from beui's Animated Breadcrumb
   (beui.dev/components/motion/breadcrumb). Same parts, no Tailwind: styling
   is the `.bc-*` block in shell.css.

     <Breadcrumb>
       <BreadcrumbList>
         <BreadcrumbItem key="home">
           <BreadcrumbLink render={(p) => <Link {...p} to="/home" />}>…</BreadcrumbLink>
         </BreadcrumbItem>
         <BreadcrumbItem key="manage-leave">
           <BreadcrumbSeparator />
           <BreadcrumbPage>Manage Leave</BreadcrumbPage>
         </BreadcrumbItem>
       </BreadcrumbList>
     </Breadcrumb>

   Keep <Breadcrumb> mounted while the route changes and key each item by its
   route: an item that stays put springs to its new position, one that
   arrives rises and fades in, one that leaves sinks and fades out (popLayout,
   so it does not hold its slot open while it goes). Leaving items are inert
   and hidden from assistive tech. Reduced motion fades without the slide and
   turns the position spring off.

   Not ported: the ellipsis dropdown that folds a long middle away. No trail
   in this app is deeper than four, the length beui starts folding at.
   ========================================================================== */

/** A navigation landmark. Keep it mounted while the route changes. */
export const Breadcrumb = ({ className = "", children, ...props }) => (
  <nav aria-label="Breadcrumb" {...props} className={("bc " + className).trim()}>
    <LayoutGroup>{children}</LayoutGroup>
  </nav>
);

/** Pass keyed BreadcrumbItems directly so entering and leaving routes animate. */
export const BreadcrumbList = ({ className = "", children, ...props }) => (
  <ol {...props} className={("bc-list " + className).trim()}>
    <AnimatePresence initial={false} mode="popLayout">
      {Children.toArray(children)}
    </AnimatePresence>
  </ol>
);

/** Use a stable route key; put its optional separator inside this item. */
export const BreadcrumbItem = forwardRef(function BreadcrumbItem(
  { className = "", style, children, ...props },
  ref
) {
  const reduce = useReducedMotion();
  const present = useIsPresent();
  const itemRef = useRef(null);

  useLayoutEffect(() => {
    const item = itemRef.current;
    if (!item || !present) return undefined;
    // popLayout snapshots offsetWidth (whole pixels); keep the exact width so
    // a lost fraction cannot wrap the last character while it leaves
    const measure = () =>
      item.style.setProperty(
        "--bc-exit-width",
        `${item.getBoundingClientRect().width}px`
      );
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(item);
    return () => observer.disconnect();
  }, [present]);

  const hidden = { opacity: 0, y: reduce ? 0 : 6 };
  return (
    <motion.li
      ref={(node) => {
        itemRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      }}
      layout={reduce ? false : "position"}
      initial={hidden}
      animate={{ opacity: 1, y: 0 }}
      exit={{ ...hidden, transition: spring.moderate.exit }}
      transition={{ ...spring.moderate, layout: spring.moderate }}
      {...props}
      inert={present ? undefined : ""}
      aria-hidden={present ? undefined : true}
      style={{
        ...style,
        minWidth: present ? style?.minWidth : "var(--bc-exit-width)",
        pointerEvents: present ? style?.pointerEvents : "none",
      }}
      className={("bc-item " + className).trim()}
    >
      {children}
    </motion.li>
  );
});

/** `render` draws your router's Link, spreading these props onto it. */
export const BreadcrumbLink = ({ className = "", render, ...props }) => {
  const linkProps = { ...props, className: ("bc-link " + className).trim() };
  return render ? render(linkProps) : <a {...linkProps}>{props.children}</a>;
};

export const BreadcrumbPage = ({ className = "", children, ...props }) => (
  <span
    {...props}
    aria-current="page"
    className={("bc-page " + className).trim()}
  >
    {children}
  </span>
);

/** Decorative separator, placed inside the following BreadcrumbItem. */
export const BreadcrumbSeparator = ({ className = "", children, ...props }) => (
  <span
    {...props}
    aria-hidden="true"
    className={("bc-separator " + className).trim()}
  >
    {children ?? <FiChevronRight />}
  </span>
);

export default Breadcrumb;
